/* JCI - Cart Drawer: quantity, remove, and real shipping cost.
   Separate module because script.cart.js loads only on the cart page and
   assumes #cart-table / #total-cart-top exist, which they do not here.

   Network policy: nothing is fetched on page load. /cart/shipping_rates.json
   responds with Clear-Site-Data: prefetchCache, prerenderCache, so calling it
   on every page wiped the theme's link prefetching and slowed navigation.
   Rates are requested only when the drawer is actually opened. */
(function () {
  'use strict';

  var FREE_THRESHOLD = 50000; // 500 ILS in agorot
  var LIGHT_BASE_RATE = 4000; // 40 ILS in agorot
  var ADDRESS = { country: 'Israel', province: '', zip: '' };

  var DRAWER = '#cart-dropdown';
  var HOSTS = '.cart-style-drawer, #cart-count-desktop, #cart-count-mobile';

  var busy = false;
  var rateCache = {};
  var wasOpen = false;

  /* Last painted state. On rotation the theme rebuilds the drawer and wipes
     our container; keeping this lets us repaint from memory with no network. */
  var lastRender = null;

  var observedDrawer = null;
  var contentObserver = null;
  var openObserver = null;

  function drawer() { return document.querySelector(DRAWER); }

  function setBusy(on) {
    var b = document.getElementById('ajaxBusy');
    if (b) { b.style.display = on ? 'block' : 'none'; }
  }

  function money(cents) {
    if (window.Shopify && Shopify.formatMoney && window.theme && theme.moneyFormat) {
      return Shopify.formatMoney(cents, theme.moneyFormat).replace(/((,00)|(\.00))$/g, '');
    }
    return (cents / 100).toFixed(2);
  }

  function toCents(price) {
    if (typeof price === 'number') { return Math.round(price); }
    var s = String(price);
    if (s.indexOf('.') > -1 || s.indexOf(',') > -1) {
      return Math.round(parseFloat(s.replace(',', '.')) * 100);
    }
    return parseInt(s, 10);
  }

  function isDrawerOpen() {
    var d = drawer();
    if (!d) { return false; }
    var host = d.closest(HOSTS);
    if (host && (host.classList.contains('open') || host.classList.contains('hover'))) {
      return true;
    }
    var hosts = document.querySelectorAll(HOSTS);
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].classList.contains('open')) { return true; }
    }
    return false;
  }

  function closeDrawer() {
    Array.prototype.forEach.call(document.querySelectorAll(HOSTS), function (el) {
      el.classList.remove('open');
      el.classList.remove('hover');
    });
  }

  /* Owns the shipping slot at runtime instead of editing the 49KB
     header.liquid. Replaces whatever snippets/shipping.liquid rendered inside
     .shipping_msg, which compares the whole cart total against the free
     shipping threshold and so promises free delivery on carts holding heavy
     items. Costs no network - safe to call as often as needed. */
  function shippingHost() {
    var d = drawer();
    if (!d) { return null; }

    var existing = d.querySelector('[data-jci-shipping]');
    if (existing) { return existing; }

    var slot = d.querySelector('.shipping_msg');
    if (!slot) {
      var buttons = d.querySelector('.buttons');
      if (!buttons) { return null; }
      slot = document.createElement('div');
      slot.className = 'shipping_msg bottompad-half clearfix';
      buttons.insertBefore(slot, buttons.firstChild);
    }

    slot.innerHTML = '<div class="jci-shipping" data-jci-shipping aria-live="polite" role="status"></div>';
    return slot.querySelector('[data-jci-shipping]');
  }

  /* The mobile cart badge sits outside every [data-js-render] region, so
     region replacement alone leaves it stale. Values still come from the
     server, so the badge cannot drift from the drawer. */
  function syncCounters(doc) {
    var freshCount = doc.querySelector('.cartCountSelector');
    var freshTotal = doc.querySelector('.cartTotalSelector');

    if (freshCount) {
      var count = parseInt(freshCount.textContent, 10);
      Array.prototype.forEach.call(document.querySelectorAll('.cartCountSelector'), function (el) {
        el.textContent = freshCount.textContent;
        if (isNaN(count) || count < 1) { el.classList.add('hidden'); }
        else { el.classList.remove('hidden'); }
      });
    }

    if (freshTotal) {
      Array.prototype.forEach.call(document.querySelectorAll('.cartTotalSelector'), function (el) {
        el.textContent = freshTotal.textContent;
      });
    }
  }

  function refreshDrawer() {
    return fetch(window.location.pathname + window.location.search, {
      cache: 'no-store',
      headers: { 'Accept': 'text/html' }
    })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector(DRAWER);
        var current = drawer();
        if (!fresh || !current) {
          console.warn('[jci-cart-drawer] drawer not found in refreshed markup');
          return;
        }

        Array.prototype.forEach.call(document.querySelectorAll('[data-js-render]'), function (node) {
          var key = node.getAttribute('data-js-render');
          if (key.indexOf('cart-upsell') === 0) { return; } // would kill the swiper
          var next = doc.querySelector('[data-js-render="' + key + '"]');
          if (next) { node.innerHTML = next.innerHTML; }
        });

        syncCounters(doc);

        current.setAttribute('data-items', fresh.getAttribute('data-items') || '0');
        current.setAttribute('data-total', fresh.getAttribute('data-total') || '');

        shippingHost();  // the refresh restored the theme's message; clear it
        bindObservers(); // regions were replaced, so rebind if needed
      })
      .catch(function (err) { console.error('[jci-cart-drawer] refresh failed:', err); });
  }

  function changeLine(key, quantity) {
    if (busy || !key) { return; }
    busy = true;
    setBusy(true);

    fetch(theme.routes_cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart.item_count === 0) {
          closeDrawer();
          var d = drawer();
          if (d) { d.classList.add('hidden'); }
        }
        return refreshDrawer();
      })
      .then(function () { return updateShipping(); })
      .catch(function (err) { console.error('[jci-cart-drawer] change failed:', err); })
      .then(function () { busy = false; setBusy(false); });
  }

  function renderShipping(host, cart, cents) {
    lastRender = (cents === null) ? null : { cart: cart, cents: cents };

    if (cents === null) { host.innerHTML = ''; return; }
    var html = '';

    if (cents === 0) {
      html += '<div class="jci-ship jci-ship--free"><span class="jci-ship__label">משלוח</span>';
      html += '<strong class="jci-ship__value">חינם</strong></div>';
    } else {
      html += '<div class="jci-ship"><span class="jci-ship__label">משלוח</span>';
      html += '<strong class="jci-ship__value">' + money(cents) + '</strong></div>';

      /* Progress bar only when the rate proves the cart is light-only. A cart
         holding a heavy item never qualifies for free shipping, so showing the
         bar for it would be a promise that cannot be kept. */
      if (cents <= LIGHT_BASE_RATE && cart.total_price < FREE_THRESHOLD) {
        var left = FREE_THRESHOLD - cart.total_price;
        var pct = Math.max(0, Math.min(100, (cart.total_price / FREE_THRESHOLD) * 100));
        html += '<div class="jci-ship__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + Math.round(pct) + '">';
        html += '<div class="jci-ship__bar-fill" style="width:' + pct.toFixed(1) + '%"></div></div>';
        html += '<p class="jci-ship__hint">עוד <strong>' + money(left) + '</strong> למשלוח חינם</p>';
      }
    }

    html += '<p class="jci-ship__note">התעריף מחושב לאזור המרכז. באזורים מרוחקים תיתכן תוספת הובלה. הסכום הסופי יוצג בקופה.</p>';
    host.innerHTML = html;
  }

  function updateShipping() {
    var host = shippingHost();
    if (!host) { return Promise.resolve(); }

    return fetch('/cart.js', { cache: 'no-store', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.item_count) { host.innerHTML = ''; lastRender = null; return; }

        var cacheKey = cart.token + ':' + cart.item_count + ':' + cart.total_price;
        if (rateCache.hasOwnProperty(cacheKey)) {
          renderShipping(host, cart, rateCache[cacheKey]);
          return;
        }

        host.setAttribute('data-loading', 'true');
        var qs = 'shipping_address%5Bcountry%5D=' + encodeURIComponent(ADDRESS.country) +
                 '&shipping_address%5Bprovince%5D=' + encodeURIComponent(ADDRESS.province) +
                 '&shipping_address%5Bzip%5D=' + encodeURIComponent(ADDRESS.zip);

        return fetch('/cart/shipping_rates.json?' + qs, { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var rates = (data && data.shipping_rates) || [];
            var min = null;
            rates.forEach(function (rate) {
              var c = toCents(rate.price);
              if (!isNaN(c) && (min === null || c < min)) { min = c; }
            });
            rateCache[cacheKey] = min;
            renderShipping(host, cart, min);
          })
          .catch(function (err) {
            console.error('[jci-cart-drawer] shipping rates failed:', err);
            host.innerHTML = '';
          })
          .then(function () { host.removeAttribute('data-loading'); });
      })
      .catch(function (err) { console.error('[jci-cart-drawer] cart fetch failed:', err); });
  }

  function lineKeyOf(el) {
    var row = el.closest('[data-jci-key]');
    return row ? row.getAttribute('data-jci-key') : null;
  }

  /* Delegated on document, so handlers survive drawer re-renders and the
     theme relocating the drawer between the desktop and mobile cart buttons. */
  document.addEventListener('click', function (ev) {
    var stepBtn = ev.target.closest('[data-jci-step]');
    if (stepBtn && stepBtn.closest(DRAWER)) {
      ev.preventDefault();
      var row = stepBtn.closest('[data-jci-key]');
      var input = row && row.querySelector('[data-jci-input]');
      if (!input) { return; }
      var next = parseInt(input.value, 10) + parseInt(stepBtn.getAttribute('data-jci-step'), 10);
      var max = input.getAttribute('max');
      if (next < 0) { return; }
      if (max !== null && max !== '' && next > parseInt(max, 10)) { return; }
      input.value = next;
      changeLine(lineKeyOf(stepBtn), next);
      return;
    }

    var removeBtn = ev.target.closest('[data-jci-remove]');
    if (removeBtn && removeBtn.closest(DRAWER)) {
      ev.preventDefault();
      changeLine(lineKeyOf(removeBtn), 0);
    }
  });

  var typeTimer;
  document.addEventListener('input', function (ev) {
    var input = ev.target.closest('[data-jci-input]');
    if (!input || !input.closest(DRAWER)) { return; }
    clearTimeout(typeTimer);
    typeTimer = setTimeout(function () {
      var v = parseInt(input.value, 10);
      if (isNaN(v) || v < 0) { return; }
      var max = input.getAttribute('max');
      if (max !== null && max !== '' && v > parseInt(max, 10)) {
        v = parseInt(max, 10);
        input.value = v;
      }
      changeLine(lineKeyOf(input), v);
    }, 600);
  });

  /* Observers are rebindable. menuAdjust() rebuilds the drawer on resize, so
     anything bound to the old node would silently stop firing. */
  function bindObservers() {
    var hosts = document.querySelectorAll(HOSTS);
    if (hosts.length) {
      if (openObserver) { openObserver.disconnect(); }
      openObserver = new MutationObserver(function () {
        var open = isDrawerOpen();
        if (open && !wasOpen) { updateShipping(); }
        wasOpen = open;
      });
      Array.prototype.forEach.call(hosts, function (h) {
        openObserver.observe(h, { attributes: true, attributeFilter: ['class'] });
      });
    }

    var d = drawer();
    if (d && d !== observedDrawer) {
      if (contentObserver) { contentObserver.disconnect(); }
      var timer;
      contentObserver = new MutationObserver(function (mutations) {
        if (busy || !isDrawerOpen()) { return; }
        var external = mutations.some(function (m) {
          var el = m.target.nodeType === 1 ? m.target : m.target.parentElement;
          return !el || !el.closest('[data-jci-shipping]');
        });
        if (!external) { return; }
        clearTimeout(timer);
        timer = setTimeout(function () { updateShipping(); }, 300);
      });
      contentObserver.observe(d, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-items', 'data-total']
      });
      observedDrawer = d;
    }
  }

  /* Rotation and resize both run the theme's menuAdjust(), which rebuilds the
     drawer and discards our container. Repaint from lastRender so this costs
     no network at all. */
  var reflowTimer;
  function handleReflow() {
    clearTimeout(reflowTimer);
    reflowTimer = setTimeout(function () {
      bindObservers();

      var d = drawer();
      if (!d) { return; }

      var host = d.querySelector('[data-jci-shipping]');
      if (host && host.innerHTML !== '') { return; } // survived, nothing to do

      host = shippingHost();
      if (!host) { return; }

      if (lastRender) {
        renderShipping(host, lastRender.cart, lastRender.cents);
      } else if (isDrawerOpen()) {
        updateShipping();
      }

      wasOpen = isDrawerOpen();
    }, 250);
  }

  window.addEventListener('resize', handleReflow);
  window.addEventListener('orientationchange', handleReflow);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) { handleReflow(); }
  });

  function init() {
    if (!drawer()) { return; }
    shippingHost();      // clears the inherited message, no network
    wasOpen = isDrawerOpen();
    if (wasOpen) { updateShipping(); }
    bindObservers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('jci:cart:updated', function () { updateShipping(); });
})();
